import { MapPin, QrCode, Edit2, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import type { PatrolCheckpoint } from '@yawi/types';
import { useI18n } from '@/shared/i18n';

interface CheckpointListProps {
  checkpoints: PatrolCheckpoint[];
  onEdit?: (checkpoint: PatrolCheckpoint) => void;
  onToggleActive?: (checkpoint: PatrolCheckpoint) => void;
  onDelete?: (checkpoint: PatrolCheckpoint) => void;
  onShowQR?: (checkpoint: PatrolCheckpoint) => void;
  showActions?: boolean;
}

export const CheckpointList = ({
  checkpoints,
  onEdit,
  onToggleActive,
  onDelete,
  onShowQR,
  showActions = true,
}: CheckpointListProps) => {
  const { t } = useI18n();

  return (
    <div className="space-y-2">
      {checkpoints.map((checkpoint, index) => (
        <div
          key={checkpoint.id}
          className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
            checkpoint.isActive
              ? 'bg-white border-slate-200'
              : 'bg-slate-50 border-slate-200 opacity-60'
          }`}
        >
          {/* Order number */}
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-700 rounded-full text-sm font-semibold">
            {checkpoint.orderIndex}
          </div>

          {/* Checkpoint info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-800 truncate">{checkpoint.name}</span>
              {!checkpoint.isActive && (
                <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full">
                  {t('common.inactive')}
                </span>
              )}
            </div>
            {checkpoint.location && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{checkpoint.location}</span>
              </div>
            )}
            {checkpoint.description && (
              <p className="text-xs text-slate-400 mt-1 truncate">{checkpoint.description}</p>
            )}
          </div>

          {/* Actions */}
          {showActions && (
            <div className="flex items-center gap-1">
              {onShowQR && (
                <button
                  onClick={() => onShowQR(checkpoint)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  title={t('patrol.downloadQR')}
                >
                  <QrCode className="w-4 h-4 text-slate-500" />
                </button>
              )}
              {onEdit && (
                <button
                  onClick={() => onEdit(checkpoint)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  title={t('common.edit')}
                >
                  <Edit2 className="w-4 h-4 text-slate-500" />
                </button>
              )}
              {onToggleActive && (
                <button
                  onClick={() => onToggleActive(checkpoint)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  title={checkpoint.isActive ? t('common.deactivate') : t('common.activate')}
                >
                  {checkpoint.isActive ? (
                    <ToggleRight className="w-5 h-5 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(checkpoint)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('common.delete')}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              )}
            </div>
          )}
        </div>
      ))}

      {checkpoints.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>{t('patrol.noCheckpoints')}</p>
        </div>
      )}
    </div>
  );
};
